import React from 'react';
import { Box, Typography, Grid, Card, Table, TableBody, TableCell, TableContainer, TableRow, Paper } from '@mui/material';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import OpacityIcon from '@mui/icons-material/Opacity';
import BoltIcon from '@mui/icons-material/Bolt';
import ScienceIcon from '@mui/icons-material/Science';
import GrassIcon from '@mui/icons-material/Grass';

interface SoilMetric {
  value: number | string;
  unit: string;
  range: string;
  label: string;
}

interface SoilSensorData {
  [key: string]: SoilMetric;
}

interface SoilSensorCardProps {
  data: SoilSensorData;
}

const SoilSensorCard: React.FC<SoilSensorCardProps> = ({ data }) => {
  const isNotApplicable = !data;

  // Use mock metrics if data is null to show the background structure
  const metrics = !isNotApplicable ? Object.values(data) : [
    { label: 'Soil Moisture', value: '--', unit: '%', range: '35-55%' },
    { label: 'Soil Temperature', value: '--', unit: '°C', range: '20-30°C' },
    { label: 'Soil pH', value: '--', unit: 'pH', range: '6.5-8.5' },
    { label: 'Elec. Conductivity', value: '--', unit: 'dS/m', range: '0.5-2.5 dS/m' },
    { label: 'Available Nitrogen', value: '--', unit: 'mg/kg', range: '0.5-1.5 mg/kg' },
  ];

  return (
    <Card sx={{
      boxShadow: "0px 0px 5px 0px rgba(0, 0, 0, 0.1019607843) !important",
      borderRadius: '8px',
      backgroundColor: '#fff',
      mt: 3,
      p: 2,
      position: 'relative', // Necessary for absolute overlay
      overflow: 'hidden'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {/* <ScienceIcon sx={{ color: '#2E7D32' }} /> */}
        <Typography sx={{ fontSize: "18px", fontWeight: 600, color: '#151E15' }}>
          Soil Sensor Data (0–15 cm)
        </Typography>
      </Box>
      <Box className='divider' sx={{ mb: 2, height: '1px', backgroundColor: '#eee' }} />

      <Box sx={{ position: 'relative' }}>
        <TableContainer component={Box} sx={{ filter: isNotApplicable ? 'blur(1px)' : 'none', opacity: isNotApplicable ? 0.6 : 1 }}>
          <Table size="small">
            <TableBody>
              <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                <TableCell sx={{ fontWeight: 600 }}>Metric (Sensor)</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Current Value</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Optimal Range</TableCell>
              </TableRow>
              {metrics.map((metric, index) => (
                <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ color: '#666' }}>
                    {metric.label}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500, color: '#151E15' }}>
                    {metric.value} {metric.unit}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#888', fontSize: '0.85rem' }}>
                    {metric.range}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {isNotApplicable && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.4)',
              borderRadius: '4px'
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#151E15', opacity: 0.8 }}>
              Site Not Applicable
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default SoilSensorCard;
